import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  List,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  DeletePerformanceReviewOwnerDocument,
  OwnersSectionFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface OwnerItemProps {
  hasOnlyOneOwner: boolean
  owner: OwnersSectionFragment
}

export const OwnerItem = ({ hasOnlyOneOwner, owner }: OwnerItemProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [open, toggle] = useToggle()

  const currentUserIsOwner = owner.access && owner.access.current

  const [deletePerformanceReviewOwner, { loading: deleting }] = useMutation(
    DeletePerformanceReviewOwnerDocument,
    {
      extractorFn: (data) => data.performanceMutations.deletePerformanceReviewOwner,
    }
  )

  const handleDeleteOwner = async (ownerIdToRemove: number) => {
    const response = await deletePerformanceReviewOwner({
      variables: {
        id: Number(ownerIdToRemove),
      },
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
      currentUserIsOwner && navigate(navigation.performanceProcess.reviews.fullPath())
    }
  }

  return (
    <List.Item key={owner.id}>
      {owner.access && (
        <Box flexDirection="row" alignItems="center" gap="s16">
          <Avatar size="s32" url={buildUrl(owner.access.avatar)} label={owner.access.fullName} />
          <Text.Normal weight="medium">
            {owner.access.fullName}{' '}
            {currentUserIsOwner &&
              i18n.t('performance_process.reviews.basic_information_section.owners.you')}
          </Text.Normal>
        </Box>
      )}
      {hasOnlyOneOwner ? (
        <Tooltip.Alt
          attachment="bottom center"
          open={open}
          toggle={toggle}
          label={i18n.t(
            'performance_process.reviews.basic_information_section.owners.one_owner_required'
          )}
        >
          <Button type="secondary" disabled iconName="delete" />
        </Tooltip.Alt>
      ) : (
        <Button
          type="secondary"
          disabled={deleting}
          onClick={() => handleDeleteOwner(owner.id)}
          iconName="delete"
        />
      )}
    </List.Item>
  )
}
